import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Modal from "react-modal";

import Chat from "../chat/Chat";

// Modal styles with Tailwind-inspired approach
const customStyles = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        width: "90vw",
        maxWidth: "1200px",
        maxHeight: "90vh",
        overflow: "hidden",
        borderRadius: "0.75rem",
        padding: "0",
        background: "#fff",
        boxShadow:
            "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        border: "none",
        zIndex: 1200,
    },
    overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
        zIndex: 1100,
    },
};

const CampaignChatModal = ({ open, onClose, campaignId, entrepreneurUser }) => {
    const [investors, setInvestors] = useState([]);
    const [selectedInvestor, setSelectedInvestor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if(open && campaignId) {
            setLoading(true);
            setError("");
            fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/investors`,
            )
                .then((res) => res.json())
                .then((data) => {
                    const formatted = data.map((inv) => ({
                        ...inv,
                        username:
                            `${inv.first_name || ""} ${
                                inv.last_name || ""
                            }`.trim() ||
                            inv.email ||
                            "Unknown",
                    }));
                    console.log("Investors data:", formatted);
                    setInvestors(formatted);
                    // If there's only one investor, select it automatically
                    if(formatted.length === 1) {
                        setSelectedInvestor(formatted[0]);
                    } else if(formatted.length > 1) {
                        handleSelectInvestor({
                            id: "all",
                            username: "All Investors",
                            type: "broadcast",
                        });
                    }
                })
                .catch(() => setError("Failed to load investors"))
                .finally(() => setLoading(false));
        }
    }, [open, campaignId]);

    const handleSelectInvestor = (investor) => {
        setSelectedInvestor(investor);
    };

    return (
        <Modal
            isOpen={open}
            onRequestClose={onClose}
            style={customStyles}
            ariaHideApp={false}
            contentLabel="Chat with Investors"
        >
            <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-800">
                        Chat with Investors
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none text-2xl"
                    >
                        &times;
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-6">
                    {loading && (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    )}
                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg
                                        className="h-5 w-5 text-red-500"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">
                                        {error}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}{" "}
                    {!loading && investors.length > 0 && (
                        <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Investor:
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {investors.length > 1 && (
                                    <button
                                        key="all"
                                        onClick={() =>
                                            handleSelectInvestor({
                                                id: "all",
                                                username: "All Investors",
                                                type: "broadcast",
                                            })
                                        }
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border-2
                                            ${
                                                selectedInvestor?.id === "all"
                                                    ? "bg-navy text-white border-navy"
                                                    : "bg-white text-navy border-navy hover:bg-blue-50"
                                            }`}
                                    >
                                        All Investors
                                    </button>
                                )}
                                {investors.map((inv) => (
                                    <button
                                        key={inv.id}
                                        onClick={() =>
                                            handleSelectInvestor(inv)
                                        }
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border-2
                                            ${
                                                selectedInvestor?.id === inv.id
                                                    ? "bg-navy text-white border-navy"
                                                    : "bg-white text-navy border-navy hover:bg-blue-50"
                                            }`}
                                    >
                                        {inv.first_name || inv.last_name
                                            ? `${inv.first_name || ""} ${
                                                  inv.last_name || ""
                                              }`.trim()
                                            : inv.email || "Investor"}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {selectedInvestor && (
                        <div className="border-t border-gray-200 pt-6 mt-6 h-[60vh] overflow-auto rounded-lg bg-white shadow-inner">
                            <Chat
                                user={entrepreneurUser}
                                otherUser={selectedInvestor}
                                contextType="campaign"
                                contextId={campaignId}
                                investors={investors}
                            />
                        </div>
                    )}
                    {!loading && investors.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                            <svg
                                className="h-12 w-12 mb-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="1.5"
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <p className="text-lg">
                                No investors have invested in this campaign yet.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

CampaignChatModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    entrepreneurUser: PropTypes.object,
};

export default CampaignChatModal;
